import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings, pick } from '@/contexts/SiteSettingsContext';
import { Shield, Users, Clock, Globe, Cpu, Wifi, CheckCircle } from 'lucide-react';
import useReveal from '@/hooks/useReveal';

const SERVICE_IMG = "https://images.pexels.com/photos/6957083/pexels-photo-6957083.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AboutPage() {
  useReveal();
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const isAr = lang === 'ar';

  const aboutStory = pick(isAr ? settings?.content?.about_story_ar : settings?.content?.about_story_en, t('about', 'story'));

  const defaultDiffs = [
    { icon: Shield, title: t('about', 'diff1'), desc: t('about', 'diff1d') },
    { icon: Users, title: t('about', 'diff2'), desc: t('about', 'diff2d') },
    { icon: Clock, title: t('about', 'diff3'), desc: t('about', 'diff3d') },
    { icon: Cpu, title: t('about', 'diff4'), desc: t('about', 'diff4d') },
    { icon: Globe, title: t('about', 'diff5'), desc: t('about', 'diff5d') },
    { icon: Wifi, title: t('about', 'diff6'), desc: t('about', 'diff6d') },
  ];
  const ICON_MAP = { shield: Shield, users: Users, clock: Clock, cpu: Cpu, globe: Globe, wifi: Wifi, check: CheckCircle };
  const fromCms = Array.isArray(settings?.content?.differentiators) ? settings.content.differentiators.filter(d => d && (d.title_en || d.title_ar)) : [];
  const diffs = fromCms.length > 0
    ? fromCms.map(d => ({
        icon: ICON_MAP[d.icon_key] || Shield,
        title: isAr ? (d.title_ar || d.title_en) : (d.title_en || d.title_ar),
        desc: isAr ? (d.desc_ar || d.desc_en) : (d.desc_en || d.desc_ar),
      }))
    : defaultDiffs;

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#071E2C] pt-32 pb-24 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(201,168,76,0.15),transparent_60%)] pointer-events-none" />
        <div className="absolute inset-x-6 lg:inset-x-12 top-24 h-px bg-gradient-to-r from-transparent via-[#A68966]/30 to-transparent" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="gmre-overline mb-6 gmre-reveal">{lang === 'ar' ? 'قصتنا · تأسست 2023' : 'Our Story · Est. 2023'}</div>
              <h1
                className="text-4xl sm:text-5xl lg:text-[56px] font-semibold text-white mb-6 tracking-tight gmre-reveal gmre-reveal-delay-1"
                style={{ fontFamily: displayFont, lineHeight: lang === 'ar' ? '1.45' : '1.08', textWrap: 'balance' }}
              >
                {t('about', 'title')}
              </h1>
              <div className="gmre-ornament justify-start mb-7 gmre-reveal gmre-reveal-delay-2"><div className="line" style={{ width: 72 }} /><div className="dot" /></div>
              <p
                className="text-white/75 text-base lg:text-lg leading-relaxed gmre-reveal gmre-reveal-delay-3 font-light"
                style={{ fontFamily: bodyFont, lineHeight: lang === 'ar' ? '2' : '1.85' }}
              >
                {aboutStory}
              </p>
            </div>
            <div className="relative gmre-reveal gmre-reveal-delay-2">
              <img
                src={SERVICE_IMG}
                alt="Guleed Mortgage Office"
                className="w-full object-cover h-[420px] rounded-sm shadow-2xl"
              />
              {/* Bronze frame accent */}
              <div className="absolute -bottom-3 -end-3 w-32 h-32 border-2 border-[#C9A84C]/60 -z-10 rounded-sm" />
              <div className="absolute -top-3 -start-3 w-32 h-32 border-2 border-[#A68966]/40 -z-10 rounded-sm" />
              {/* Glass badge floating */}
              <div className="absolute -bottom-6 start-6 lg:start-10 gmre-glass rounded-2xl px-5 py-3 flex items-center gap-3 max-w-xs">
                <div className="w-9 h-9 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/40 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-[#C9A84C]" />
                </div>
                <div>
                  <p className="text-[10px] text-white/55 tracking-[0.22em] uppercase">{lang === 'ar' ? 'منذ' : 'Established'}</p>
                  <p className="text-sm font-semibold text-white">{lang === 'ar' ? '3+ سنوات · الإمارات' : '3+ Years · UAE'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="bg-[#F8FAFB] py-24 lg:py-32 relative">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 gmre-reveal">
            <div className="gmre-overline justify-center mb-4">{lang === 'ar' ? 'لماذا جُليد' : 'What Sets Us Apart'}</div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2F44] tracking-tight"
              style={{ fontFamily: displayFont }}
            >
              {lang === 'ar' ? 'لماذا جُليد؟' : 'Why Choose Guleed Mortgage?'}
            </h2>
            <div className="gmre-ornament mt-6"><div className="line" /><div className="dot" /><div className="line" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {diffs.map((d, i) => (
              <div
                key={i}
                data-testid={`diff-card-${i}`}
                className={`gmre-glass-light rounded-2xl p-8 group gmre-reveal gmre-reveal-delay-${Math.min((i % 5) + 1, 5)}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0B2F44] to-[#071E2C] flex items-center justify-center mb-6 group-hover:from-[#C9A84C] group-hover:to-[#A68966] transition-all duration-500 shadow-lg shadow-[#0B2F44]/20">
                  <d.icon className="w-5 h-5 text-[#C9A84C] group-hover:text-[#0B2F44] transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-semibold text-[#0B2F44] text-lg mb-2.5 tracking-tight" style={{ fontFamily: displayFont }}>{d.title}</h3>
                <p className="text-[#4A6572] text-sm leading-relaxed" style={{ lineHeight: lang === 'ar' ? '2' : '1.75' }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
